import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Typography, Chip, LinearProgress, Tooltip } from '@mui/material';
import {
  ACTIVE_RENDER_STATUSES,
  PIPELINE_CHIP_SX,
  PIPELINE_RENDER_LABELS,
  PIPELINE_RENDER_COLORS,
  VOICEOVER_STATUS_CHIP,
} from '../sleep-videos-v3/constants';
import { getVoiceOverStatus, getRenderProgress } from '../sleep-videos-v3/pipelineUtils';
import { AI_STORIES_STATUS_CHIP, labelForAiStoriesProgressStep } from './constants';
import { formatEastern } from '../../utils/easternTime';

function scriptChipForRow(row) {
  const base = AI_STORIES_STATUS_CHIP[row.status] || {
    label: row.status || '—',
    color: 'default',
  };
  if (row.status === 'script_generating' && row.progress?.currentStep) {
    const step = labelForAiStoriesProgressStep(row.progress.currentStep);
    return { ...base, label: `${base.label} · ${step}` };
  }
  return base;
}

export default function AiStoriesPipelineStatusCell({ row, now: nowProp, sx }) {
  const now = nowProp ?? Date.now();
  const scriptChip = scriptChipForRow(row);
  const progress = row.progress;
  const hasScriptStepProgress =
    row.status === 'script_generating' &&
    typeof progress?.totalSteps === 'number' &&
    progress.totalSteps > 0 &&
    typeof progress?.stepNumber === 'number' &&
    progress.stepNumber > 0;
  const scriptProgressPercent = hasScriptStepProgress
    ? Math.min(100, (progress.stepNumber / progress.totalSteps) * 100)
    : null;

  const scriptGeneratingWithBar =
    row.status === 'script_generating' && scriptProgressPercent != null;

  const voStatus = getVoiceOverStatus(row);
  const voChip = VOICEOVER_STATUS_CHIP[voStatus] || VOICEOVER_STATUS_CHIP.idle;
  const renderStatus = row.renderStatus?.status || 'idle';
  const renderProgressInfo = getRenderProgress(
    row.renderStatus,
    row.voiceOverDuration,
    now,
  );
  const renderInProgress = ACTIVE_RENDER_STATUSES.includes(renderStatus);
  const videoLabel =
    renderInProgress && renderProgressInfo?.text
      ? renderProgressInfo.text
      : (PIPELINE_RENDER_LABELS[renderStatus] ?? 'None');
  const videoColor = renderInProgress
    ? 'warning'
    : (PIPELINE_RENDER_COLORS[renderStatus] ?? 'default');

  const uploadStatus = row.youtube?.uploadStatus;
  const uploadChip = {
    queued: { label: 'Queued', color: 'default' },
    uploading: { label: `${row.youtube?.progress || 0}%`, color: 'warning' },
    retrying: { label: 'Retrying', color: 'warning' },
    failed: { label: 'Failed', color: 'error' },
    review: { label: 'Needs review', color: 'error' },
  }[uploadStatus] || { label: row.autoUploadToChannel ? 'Waiting' : 'Off', color: 'default' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, overflowX: 'auto', ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', width: 'max-content' }}>
        <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
          Script
        </Typography>
        {row.status === 'completed' ? (
          <CheckIcon color='success' fontSize='small' titleAccess='Script completed' sx={{ flexShrink: 0 }} />
        ) : scriptGeneratingWithBar ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <LinearProgress
              variant='determinate'
              value={scriptProgressPercent}
              sx={{ width: 56, height: 5, borderRadius: 1 }}
            />
            <Typography variant='caption' sx={{ flexShrink: 0, minWidth: 28 }}>
              {Math.round(scriptProgressPercent)}%
            </Typography>
          </Box>
        ) : (
          <Chip
            label={scriptChip.label}
            size='small'
            color={scriptChip.color}
            variant='outlined'
            sx={PIPELINE_CHIP_SX}
          />
        )}
        <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
          →
        </Typography>
        <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
          VO
        </Typography>
        {voStatus === 'completed' ? (
          <CheckIcon color='success' fontSize='small' titleAccess='Voice-over completed' sx={{ flexShrink: 0 }} />
        ) : (
          <Chip
            label={voChip.label}
            size='small'
            color={voChip.color}
            variant='outlined'
            sx={PIPELINE_CHIP_SX}
          />
        )}
        <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
          →
        </Typography>
        <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
          Video
        </Typography>
        {renderStatus === 'completed' ? (
          <CheckIcon color='success' fontSize='small' titleAccess='Video completed' sx={{ flexShrink: 0 }} />
        ) : (
          <Chip
            label={videoLabel}
            size='small'
            color={videoColor}
            variant='outlined'
            sx={PIPELINE_CHIP_SX}
          />
        )}
        <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
          &rarr;
        </Typography>
        <Typography variant='caption' color='text.secondary'>Upload</Typography>
        {uploadStatus === 'completed' ? (
          <CheckIcon color='success' fontSize='small' titleAccess='Upload completed' />
        ) : (
          <Tooltip title={['failed', 'review'].includes(uploadStatus) ? 'Open Channels > YouTube to review this upload.' : ''}>
            <Chip label={uploadChip.label} color={uploadChip.color} size='small' variant='outlined' sx={PIPELINE_CHIP_SX} />
          </Tooltip>
        )}
      </Box>
      {renderInProgress && renderProgressInfo?.progress != null && (
        <LinearProgress
          variant='determinate'
          value={renderProgressInfo.progress}
          sx={{ height: 4, borderRadius: 1 }}
        />
      )}
      {row.scheduledPublishAt && (
        <Typography variant='caption' color='text.secondary'>
          Publishes {formatEastern(row.scheduledPublishAt)}
        </Typography>
      )}
    </Box>
  );
}
